import { useState, useEffect, useCallback, useRef } from "react";
import { SequenceSortContent } from "../../../../../types/newtypes";
import { useScoreTracking } from "../../../../../context/ScoreTrackingContext";

interface UseSequenceSortHookProps {
  content: SequenceSortContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

interface UseSequenceSortHookReturn {
  droppedItems: (string | null)[];
  activeId: string | null;
  isCorrect: boolean;
  showFeedback: boolean;
  feedbackMessage: string;
  handleDragStart: (event: any) => void;
  handleDragEnd: (event: any) => void;
  handleTryAgain: () => void;
  handleMoveToAvailable: (itemId: string) => void;
  getDraggableItems: () => Array<{ id: string; text: string }>;
  getDropZones: () => Array<{
    id: string;
    label: string;
    matchedItem: string | null;
    isCorrect: boolean;
    isIncorrect: boolean;
    isLocked: boolean;
    isDisabled: boolean;
  }>;
}

export const useSequenceSortHook = ({
  content,
  onCardStateChange,
  cardId,
}: UseSequenceSortHookProps): UseSequenceSortHookReturn => {
  const [droppedItems, setDroppedItems] = useState<(string | null)[]>(
    new Array(content?.correct_order?.length)?.fill(null)
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [zoneFeedback, setZoneFeedback] = useState<
    Array<"correct" | "incorrect" | null>
  >(new Array(content?.correct_order?.length)?.fill(null));

  // Stable shuffled order for available items — shuffled once per cardId, not on every render.
  // Keyed on cardId (not array reference) to avoid re-shuffling when parent reconstructs content.
  const [shuffledItemIds, setShuffledItemIds] = useState<string[]>([]);
  const shuffledForCardIdRef = useRef<string | null>(null);

  const { incrementMistakes, updateCardScore, isCardCompleted, getMistakes } =
    useScoreTracking();
  const maxScore = content?.max_score ?? 0;
  const previousCardIdRef = useRef<string | null>(null);

  // Shuffle item IDs once per cardId — ignore content?.items reference changes
  useEffect(() => {
    if (shuffledForCardIdRef.current === cardId) {
      return;
    }
    shuffledForCardIdRef.current = cardId;
    const ids = (content?.items || []).map((item) => item.id);
    // Fisher-Yates shuffle
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    setShuffledItemIds(ids);
  }, [content?.items, cardId]);

  // Reset state when cardId changes (for back-to-back sequence_sort cards)
  useEffect(() => {
    // Only reset if we're actually transitioning to a different card
    if (
      previousCardIdRef.current !== null &&
      previousCardIdRef.current !== cardId
    ) {
      setDroppedItems(new Array(content?.correct_order?.length)?.fill(null));
      setActiveId(null);
      setIsCorrect(false);
      setShowFeedback(false);
      setFeedbackMessage("");
      setZoneFeedback(new Array(content?.correct_order?.length)?.fill(null));
      // Don't call onCardStateChange here as it would disable the next button
    }
    previousCardIdRef.current = cardId;
  }, [cardId, content?.correct_order?.length]);

  // Handle drag start
  const handleDragStart = useCallback(
    (event: any) => {
      setActiveId(event?.active?.id);
    },
    []
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active && typeof over.id === "string" && over.id.startsWith("drop-zone-")) {
        const dropZoneIndex = parseInt(over.id.replace("drop-zone-", ""));
        const itemId = active.id as string;

        setDroppedItems((prev) => {
          const newItems = [...prev];
          // Prevent overriding locked (correct) positions
          if (zoneFeedback[dropZoneIndex] === "correct") {
            return newItems;
          }

          // Remove the item from its previous position if it exists
          const previousIndex = newItems.findIndex((item) => item === itemId);
          if (previousIndex !== -1) {
            // Do not allow moving out of a locked position
            if (zoneFeedback[previousIndex] === "correct") {
              return newItems;
            }
            newItems[previousIndex] = null;
          }

          // Add the item to the new position
          newItems[dropZoneIndex] = itemId;

          // Evaluate correctness immediately for this position
          const isPosCorrect = content?.correct_order[dropZoneIndex] === itemId;
          setZoneFeedback((prevFeedback) => {
            const next = [...prevFeedback];
            next[dropZoneIndex] = isPosCorrect ? "correct" : "incorrect";
            return next;
          });

          if (!isPosCorrect) {
            if (!isCardCompleted(cardId)) {
              incrementMistakes(cardId, maxScore);
            }
          }

          return newItems;
        });
      }
    },
    [zoneFeedback, content?.correct_order, incrementMistakes, isCardCompleted, cardId, maxScore]
  );

  // Get draggable items (not yet dropped) — uses stable shuffled order
  const getDraggableItems = useCallback(() => {
    const itemsMap = new Map(
      (content?.items || []).map((item) => [item.id, item])
    );
    return shuffledItemIds
      .filter((id) => !droppedItems?.includes(id))
      .map((id) => {
        const item = itemsMap.get(id);
        return { id, text: item?.text || "" };
      });
  }, [content?.items, shuffledItemIds, droppedItems]);

  // Get drop zones
  const getDropZones = useCallback(() => {
    return content?.correct_order?.map((_, index) => {
      const matchedItemId = droppedItems[index];
      const matchedItem = matchedItemId
        ? content?.items?.find((item) => item.id === matchedItemId)?.text || null
        : null;

      const status = zoneFeedback[index];
      const isCorrectZone = status === "correct";
      const isIncorrectZone = status === "incorrect";
      const isLocked = isCorrectZone; // lock correct positions
      const isDisabled = isLocked;

      return {
        id: `drop-zone-${index}`,
        label: `Step ${index + 1}`,
        matchedItem,
        isCorrect: isCorrectZone,
        isIncorrect: isIncorrectZone,
        isLocked,
        isDisabled,
      };
    });
  }, [content?.correct_order, content?.items, droppedItems, zoneFeedback]);

  // Watch for completion (all positions correct)
  useEffect(() => {
    const allCorrect =
      zoneFeedback.length === (content?.correct_order?.length || 0) &&
      zoneFeedback.every((s) => s === "correct");
    if (allCorrect && !isCorrect) {
      setIsCorrect(true);
      setShowFeedback(true);
      setFeedbackMessage(content?.feedback.correct_text);
      onCardStateChange?.(true, true);
      if (!isCardCompleted(cardId)) {
        const mistakes = getMistakes(cardId) || 0;
        updateCardScore(cardId, mistakes, maxScore);
      }
    } else if (!allCorrect && isCorrect) {
      // If user modifies after completion, reset completion state
      setIsCorrect(false);
      setShowFeedback(false);
      setFeedbackMessage("");
      onCardStateChange?.(false, false);
    }
  }, [zoneFeedback, content?.correct_order?.length, content?.feedback?.correct_text, onCardStateChange, isCardCompleted, cardId, getMistakes, updateCardScore, maxScore, isCorrect]);

  // Handle try again - move wrong items back to available
  const handleTryAgain = useCallback(() => {
    setIsCorrect(false);
    setShowFeedback(false);
    setFeedbackMessage("");

    // Move wrong items back to available (clear their positions) and clear incorrect feedback
    setDroppedItems((prev) => {
      const newItems = [...prev];
      setZoneFeedback((prevFeedback) => {
        const next = [...prevFeedback];
        next.forEach((status, index) => {
          if (status === "incorrect") {
            next[index] = null;
            newItems[index] = null;
          }
        });
        return next;
      });
      return newItems;
    });

    onCardStateChange?.(false, false);
  }, [onCardStateChange]);

  // Handle move item back to available (before checking)
  const handleMoveToAvailable = useCallback((itemId: string) => {
    setDroppedItems((prev) => {
      const newItems = [...prev];
      const itemIndex = newItems.findIndex((id) => id === itemId);
      if (itemIndex !== -1 && zoneFeedback[itemIndex] !== "correct") {
        newItems[itemIndex] = null;
        setZoneFeedback((prevFeedback) => {
          const next = [...prevFeedback];
          next[itemIndex] = null;
          return next;
        });
      }
      return newItems;
    });
  }, [zoneFeedback]);

  // Reset state when content changes
  useEffect(() => {
    setDroppedItems(new Array(content?.correct_order?.length)?.fill(null));
    setActiveId(null);
    setIsCorrect(false);
    setShowFeedback(false);
    setFeedbackMessage("");
    setZoneFeedback(new Array(content?.correct_order?.length)?.fill(null));
  }, [content?.correct_order?.length]);

  return {
    droppedItems,
    activeId,
    isCorrect,
    showFeedback,
    feedbackMessage,
    handleDragStart,
    handleDragEnd,
    handleTryAgain,
    handleMoveToAvailable,
    getDraggableItems,
    getDropZones,
  };
};
